import { type BoxProps, Flex, Text } from '@mantine/core'
import { useMemo } from 'react'

import { TextCurrency } from '@flecto/ui'

import { useDocumentState } from '../../hooks/useDocumentState'
import { SectionTable } from './SectionTable'
import styles from './styles.module.css'

type SectionServicesProps = BoxProps

export function SectionSummary({ ...props }: Readonly<SectionServicesProps>) {
  const { documentPreview, translate } = useDocumentState()

  const discountAmount = useMemo(() => {
    const amountValue =
      documentPreview?.booking?.discount_type === 'discount_percentage'
        ? `${documentPreview?.discount_amount}%`
        : '€'

    return amountValue
  }, [documentPreview?.booking])

  const discountType = useMemo(() => {
    return documentPreview?.booking?.discount_type === 'discount_percentage'
      ? '%'
      : '€'
  }, [documentPreview?.booking])

  const hasCustomDiscount = useMemo(() => {
    return (
      documentPreview?.booking?.discount_type !==
        documentPreview?.customer?.default_discount_type ||
      documentPreview?.discount_amount !==
        documentPreview?.customer?.default_discount_amount
    )
  }, [documentPreview?.booking])

  const discountCents = documentPreview?.booking?.discount_cents ?? 0

  const depositCents = documentPreview?.deposit_cents ?? 0

  return (
    <SectionTable {...props}>
      <SectionTable.Header>
        <Text fw={500} size='md'>
          {translate?.('label_summary')}
        </Text>
      </SectionTable.Header>
      <SectionTable.Body>
        <Flex justify='space-between'>
          <Text size='md'>
            {translate?.('label_summary_subtotal_without_taxes')}
          </Text>
          <TextCurrency
            cents={documentPreview?.booking?.rental_subtotal_cents ?? 0}
            size='md'
          />
        </Flex>
        <Flex justify='space-between' mt='xs'>
          <Text size='md'>{translate?.('label_taxes')}</Text>
          <TextCurrency
            cents={documentPreview?.booking?.rental_vat_cents ?? 0}
            size='md'
          />
        </Flex>
        <Flex justify='space-between' mt='xs'>
          <Text size='md'>
            {translate?.('label_summary_subtotal_with_taxes')}
          </Text>
          <TextCurrency
            cents={
              documentPreview?.booking?.rental_total_without_discount_cents ?? 0
            }
            size='md'
          />
        </Flex>
        {discountCents > 0 && (
          <Flex justify='space-between' mt='xs'>
            <Text size='md'>
              {translate?.('label_discount')} (
              {!hasCustomDiscount ? discountType : discountAmount})
            </Text>
            <Flex display='inline-flex'>
              <Text size='md'>- </Text>
              <TextCurrency cents={discountCents} size='md' />
            </Flex>
          </Flex>
        )}
        <Flex justify='space-between' mt='xs' className={styles.summaryTotal}>
          <Text fw={500} size='md' data-testid='summary-total'>
            {translate?.('label_total')}
          </Text>
          <TextCurrency
            fw={500}
            cents={documentPreview?.booking?.rental_total_cents ?? 0}
            size='md'
          />
        </Flex>
        {depositCents > 0 && (
          <Flex justify='space-between' mt='xs'>
            <Text c='dimmed' size='md'>
              {translate?.('label_deposit')}
            </Text>
            <TextCurrency c='dimmed' cents={depositCents} size='md' />
          </Flex>
        )}
      </SectionTable.Body>
    </SectionTable>
  )
}
